import React from 'react'
import {MemoizeOne} from 'yougile-util'
import {
  createSubtaskFromTask,
  DataObjectId,
  Hub,
  noBoardContainerId
} from 'yougile-model'
import {Sync} from '@client/sync'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {Board} from '@extensions/board'
import {Column} from '@extensions/column'
import {SubtaskUtil} from '@extensions/subtask-util'
import {TimelineNewStore} from './timeline-new-store'
import {EnhancedDragData} from './types'

type PrepareDragData = typeof DragHelper.prototype.prepareDragData

export class DragHelper {
  prepareDragDataMemo = new MemoizeOne<PrepareDragData>()

  constructor(private readonly store: TimelineNewStore) {
    this.prepareDragData = this.prepareDragData.bind(this)
    this.onDrop = this.onDrop.bind(this)
  }

  prepareDragData(
    path: DataObjectId | string,
    overPath: DataObjectId | string,
    before: boolean
  ): EnhancedDragData | undefined {
    type Fn = typeof DragHelper.prototype.prepareDragData

    const memo = this.prepareDragDataMemo
    const params: Parameters<Fn> = [path, overPath, before]
    if (memo.isMemoized(params)) {
      return memo.getMemoized()
    }

    const result = this.prepareDragDataImpl(...params)
    memo.memoize(params, result)
    return result
  }

  private prepareDragDataImpl(
    path: DataObjectId | string,
    overPath: DataObjectId | string,
    before: boolean
  ): EnhancedDragData | undefined {
    if (path === overPath || !this.store.state.board) {
      return undefined
    }

    const draggedData = this.store.state.pathToData[path]
    if (!draggedData) {
      return undefined
    }

    const draggedType = draggedData.row.type

    if (overPath.includes('/new-task')) {
      const overRow = this.store.state.tableRows.find(
        x => x.data.path === overPath
      )
      if (!overRow) {
        return undefined
      }

      if (draggedType === 'column' && overRow.data.level === 1) {
        // dragging a column over a 'new-task' of some column means inserting a dragged column after that column
        return {
          rowId: overPath,
          position: 'after',
          draggedHubId: draggedData.row.hubId,
          overHubId: overRow.data.parentId,
          overPosition: 'after'
        }
      } else if (draggedType === 'task') {
        // forbid dragging a task into one of its children
        const allParentIds = SubtaskUtil.getAllParentTaskIds(
          overRow.data.parentId
        )
        if (allParentIds.includes(draggedData.row.hubId)) {
          return undefined
        }

        // dragging a task over a 'new-task' of some column means inserting a dragged task at the end of that column
        return {
          rowId: overPath,
          position: 'before',
          draggedHubId: draggedData.row.hubId,
          prevParentHubId: draggedData.row.parentId,
          intoHubId: overRow.data.parentId,
          overPosition: 'after'
        }
      }

      return undefined
    }

    if (overPath.includes('/new-column')) {
      if (draggedType === 'column') {
        // dragging a column over a 'new-row' means inserting it at the end of board column list
        return {
          rowId: overPath,
          position: 'before',
          draggedHubId: draggedData.row.hubId,
          intoHubId: this.store.state.board.id,
          overPosition: 'after'
        }
      }

      // a task can't be dragged on a 'new-row' row
      return undefined
    }

    const overData = this.store.state.pathToData[overPath]
    if (!overData) {
      return undefined
    }

    const overType = overData.row.type
    if (draggedType === 'column') {
      if (overType === 'column') {
        // dragging a column over an expanded column means inserting it only before that column
        let position: 'before' | 'after' = 'before'
        if (
          !this.store.getExpanded({type: overData.row.type, path: overPath})
        ) {
          // dragging a column over a collapsed column allows inserting it both before and after that column
          position = before ? 'before' : 'after'
        }

        return {
          rowId: overPath,
          position: position,
          draggedHubId: draggedData.row.hubId,
          overHubId: overData.row.hubId,
          overPosition: position
        }
      } else if (overType === 'task') {
        // dragging a column over a last task in the whole list means inserting it at the end of the board column list
        if (
          this.store.state.tableRows[this.store.state.tableRows.length - 1]
            ?.data.path === overPath
        ) {
          return {
            rowId: overPath,
            position: 'after',
            draggedHubId: draggedData.row.hubId,
            intoHubId: this.store.state.board.id,
            overPosition: 'after'
          }
        }

        // in other cases, dragging a column over a task is prohibited
        return undefined
      }

      // unknown over type
      return undefined
    } else if (draggedType === 'task') {
      if (overType === 'column') {
        // dragging a task over a column means inserting a dragged task at the top of that column
        return {
          rowId: overPath,
          position: 'after',
          draggedHubId: draggedData.row.hubId,
          prevParentHubId: draggedData.row.parentId,
          intoHubId: overData.row.hubId,
          overPosition: 'before'
        }
      } else if (overType === 'task') {
        // forbid dragging a task into one of its children
        const allParentIds = SubtaskUtil.getAllParentTaskIds(overData.row.hubId)
        if (allParentIds.includes(draggedData.row.hubId)) {
          return undefined
        }

        if (before) {
          // dragging a task before another task means inserting it before that task in a column or in subtask list
          return {
            rowId: overPath,
            position: 'before',
            draggedHubId: draggedData.row.hubId,
            prevParentHubId: draggedData.row.parentId,
            overHubId: overData.row.hubId,
            intoHubId: overData.row.parentId,
            overPosition: 'before'
          }
        } else if (
          this.store.getExpanded({type: overData.row.type, path: overPath})
        ) {
          // dragging a task after an expanded task means inserting it as a subtask at the beginning of subtask list
          return {
            rowId: overPath,
            position: 'after',
            draggedHubId: draggedData.row.hubId,
            prevParentHubId: draggedData.row.parentId,
            intoHubId: overData.row.hubId,
            overPosition: 'before'
          }
        } else {
          // dragging a task after a not expanded task means inserting it after that task
          return {
            rowId: overPath,
            position: 'after',
            draggedHubId: draggedData.row.hubId,
            prevParentHubId: draggedData.row.parentId,
            overHubId: overData.row.hubId,
            intoHubId: overData.row.parentId,
            overPosition: 'after'
          }
        }
      }

      // unknown over type
      return undefined
    }

    // unknown dragged type
    return undefined
  }

  // eslint-disable-next-line max-params
  onDrop(
    _: React.DragEvent<HTMLDivElement>,
    path: DataObjectId | string,
    overPath: DataObjectId | string,
    before: boolean
  ): void {
    const dragData = this.prepareDragData(path, overPath, before)
    this.prepareDragDataMemo.clear()

    const draggedData = this.store.state.pathToData[path]
    if (!dragData || !this.store.state.board || !draggedData) {
      return
    }

    const box = Sync.getCurrentSandbox()
    const draggedHub = box.getHub(dragData.draggedHubId)
    if (!draggedHub) {
      return
    }

    const {overPosition} = dragData
    const board = this.store.state.board
    const intoHub = box.getHub(dragData.intoHubId)
    const overHub = box.getHub(dragData.overHubId)
    const draggedType = box.getType(draggedHub)

    if (draggedType === 'column') {
      if (overHub && box.getType(overHub) === 'column') {
        this.onColumnDropOverColumn(draggedHub, board, overHub, overPosition)
      } else if (intoHub && box.getType(intoHub) === 'board') {
        this.onColumnDropIntoBoard(draggedHub, intoHub, overPosition)
      }
    } else if (draggedType === 'task') {
      if (overHub && intoHub) {
        const overType = box.getType(overHub)
        const intoType = box.getType(intoHub)
        if (overType === 'task' && intoType === 'task') {
          this.onTaskDropOverTaskSubtask(
            draggedHub,
            overHub,
            intoHub,
            overPosition
          )
        } else if (overType === 'task' && intoType === 'column') {
          this.onTaskDropOverColumnTask(
            draggedHub,
            overHub,
            intoHub,
            overPosition
          )
        }
      } else if (intoHub) {
        const intoType = box.getType(intoHub)
        if (intoType === 'column') {
          this.onTaskDropIntoColumn(draggedHub, intoHub, overPosition)
        } else if (intoType === 'task') {
          this.onTaskDropIntoTask(draggedHub, intoHub, overPosition)
        }
      }

      // Remove from previous parent task if existed and not dragging inside it
      const prevParentHub = box.getHub(dragData.prevParentHubId)
      if (
        prevParentHub &&
        box.getType(prevParentHub) === 'task' &&
        prevParentHub.id !== intoHub?.id
      ) {
        this.removeFromSubtasks(prevParentHub, draggedHub)
      }
    }

    this.store.calculateTableRows()

    // raf to compensate mouseenter being called on a previous location of the dragged row
    requestAnimationFrame(() => {
      this.store.setHoveredRow(path)
    })
  }

  private onColumnDropOverColumn(
    draggedHub: Hub,
    intoHub: Hub,
    overHub: Hub,
    overPosition: 'before' | 'after'
  ): void {
    void Tracking.send(
      TrackingEvent.ganttChangeTheOrderOfColumnsViaGanttChartTaskList
    )

    const order = Board.getColumnsOrder(intoHub)
    const draggedIx = order.order(draggedHub)
    const overIx = order.order(overHub)
    if (draggedIx > overIx) {
      const ix = overIx + (overPosition === 'before' ? 0 : 1)
      Board.columnDropHandler(ix, draggedHub, intoHub, intoHub)
    } else if (draggedIx > -1) {
      const ix = overIx + (overPosition === 'before' ? -1 : 0)
      Board.columnDropHandler(ix, draggedHub, intoHub, intoHub)
    }
  }

  private onColumnDropIntoBoard(
    draggedHub: Hub,
    intoHub: Hub,
    overPosition: 'before' | 'after'
  ): void {
    void Tracking.send(
      TrackingEvent.ganttChangeTheOrderOfColumnsViaGanttChartTaskList
    )

    const order = Board.getColumnsOrder(intoHub)
    const list = order.getList()
    const ix = overPosition === 'before' ? 0 : list.length - 1
    Board.columnDropHandler(ix, draggedHub, intoHub, intoHub)
  }

  private onTaskDropOverTaskSubtask(
    draggedHub: Hub,
    overHub: Hub,
    intoHub: Hub,
    overPosition: 'before' | 'after'
  ): void {
    const subtask = createSubtaskFromTask(draggedHub)
    if (!subtask) {
      return
    }

    void Tracking.send(
      TrackingEvent.ganttChangeTheOrderOfTasksViaGanttChartTaskList
    )

    let {order, subtasks} = SubtaskUtil.getSubtaskList(intoHub)
    order = order.filter(id => id !== draggedHub.id)
    const overIx = order.findIndex(id => id === overHub.id)
    const ix = overIx + (overPosition === 'before' ? 0 : 1)
    order.splice(ix, 0, draggedHub.id)
    subtasks = {...subtasks, [draggedHub.id]: subtask}
    SubtaskUtil.setSubtaskList(intoHub, {order, subtasks})

    this.moveToNoBoard(draggedHub)
  }

  private onTaskDropOverColumnTask(
    draggedHub: Hub,
    overHub: Hub,
    intoHub: Hub,
    overPosition: 'before' | 'after'
  ): void {
    void Tracking.send(
      TrackingEvent.ganttChangeTheOrderOfTasksViaGanttChartTaskList
    )

    const order = Column.getTasksOrder(intoHub)
    const draggedIx = order.order(draggedHub)
    const overIx = order.order(overHub)
    if (draggedIx > overIx || draggedIx === -1) {
      const ix = overIx + (overPosition === 'before' ? 0 : 1)
      Column.taskDropHandler(ix, draggedHub, intoHub, {type: 'drag'})
    } else if (draggedIx > -1) {
      const ix = overIx + (overPosition === 'before' ? -1 : 0)
      Column.taskDropHandler(ix, draggedHub, intoHub, {type: 'drag'})
    }
  }

  private onTaskDropIntoColumn(
    draggedHub: Hub,
    intoHub: Hub,
    overPosition: 'before' | 'after'
  ): void {
    void Tracking.send(
      TrackingEvent.ganttChangeTheOrderOfTasksViaGanttChartTaskList
    )

    const order = Column.getTasksOrder(intoHub)
    const list = order.getList()
    const draggedIx = order.order(draggedHub)
    if (draggedIx === -1) {
      const ix = overPosition === 'before' ? 0 : list.length
      Column.taskDropHandler(ix, draggedHub, intoHub, {type: 'drag'})
    } else {
      const ix = overPosition === 'before' ? 0 : list.length - 1
      Column.taskDropHandler(ix, draggedHub, intoHub, {type: 'drag'})
    }
  }

  private onTaskDropIntoTask(
    draggedHub: Hub,
    intoHub: Hub,
    overPosition: 'before' | 'after'
  ): void {
    const subtask = createSubtaskFromTask(draggedHub)
    if (!subtask) {
      return
    }
    void Tracking.send(
      TrackingEvent.ganttChangeTheOrderOfTasksViaGanttChartTaskList
    )

    let {order, subtasks} = SubtaskUtil.getSubtaskList(intoHub)
    order = order.filter(id => id !== draggedHub.id)
    const ix = overPosition === 'before' ? 0 : order.length
    order.splice(ix, 0, draggedHub.id)
    subtasks = {...subtasks, [draggedHub.id]: subtask}
    SubtaskUtil.setSubtaskList(intoHub, {order, subtasks})

    this.moveToNoBoard(draggedHub)
  }

  private removeFromSubtasks(taskHub: Hub, removedHub: Hub): void {
    let list = SubtaskUtil.getSubtaskList(taskHub)
    list = SubtaskUtil.deleteSubtask(list, [removedHub.id])
    SubtaskUtil.setSubtaskList(taskHub, list)
  }

  private moveToNoBoard(draggedHub: Hub): void {
    const box = Sync.get(draggedHub)
    box.moveTreeNode(
      draggedHub,
      draggedHub.location,
      noBoardContainerId,
      false,
      true
    )

    draggedHub.location = noBoardContainerId
    draggedHub.commit()
  }
}
